import { useState } from 'react';
import type { SnakeLive, SnakeTradeDot } from '@/lib/snake-market';

/** The live-overlay state for one snake series, plus its mutators. */
export interface SnakeLiveState {
  /** Latest contract prices (cents) + spot for the series. */
  live: SnakeLive;
  /** Streamed trade dots (capped, newest-last). */
  tradeDots: SnakeTradeDot[];
  /** Merge a `PriceUpdated` tick (Up/Down in cents). */
  setPrices: (upCents: number, downCents: number) => void;
  /** Merge a spot tick (dollars). */
  setSpot: (spot: number) => void;
  /** Append a streamed trade dot (capped to the most recent {@link TRADE_DOT_CAP}). */
  addTradeDot: (dot: SnakeTradeDot) => void;
}

/** Keep only the most-recent N trade dots so the chart overlay stays bounded. */
export const TRADE_DOT_CAP = 50;

const EMPTY_LIVE: SnakeLive = { upCents: null, downCents: null, spot: null };

/**
 * Holds the live overlay (contract prices + underlying spot + trade dots) for a
 * single snake series on a surface.
 *
 * Pair with a {@link SnakePriceSubscriber}: it calls `setPrices` on each
 * `PriceUpdated`, `setSpot` on each `InstrumentTick`, and `addTradeDot` on each
 * `TradeStreamed`. The read model overlays `live` on top of the API seed. Kept
 * as a tiny hook so the lobby card, page, and buy panel share the same wiring.
 */
export function useSnakeLive(): SnakeLiveState {
  const [live, setLive] = useState<SnakeLive>(EMPTY_LIVE);
  const [tradeDots, setTradeDots] = useState<SnakeTradeDot[]>([]);

  function setPrices(upCents: number, downCents: number) {
    setLive((prev) => (prev.upCents === upCents && prev.downCents === downCents ? prev : { ...prev, upCents, downCents }));
  }

  function setSpot(spot: number) {
    setLive((prev) => (prev.spot === spot ? prev : { ...prev, spot }));
  }

  function addTradeDot(dot: SnakeTradeDot) {
    setTradeDots((prev) => {
      if (prev.some((d) => d.id === dot.id)) return prev;
      const next = [...prev, dot];
      return next.length > TRADE_DOT_CAP ? next.slice(next.length - TRADE_DOT_CAP) : next;
    });
  }

  return { live, tradeDots, setPrices, setSpot, addTradeDot };
}
